import { watch, onBeforeUnmount, unref } from 'vue'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'

// Google Places address autocomplete, bolted onto an existing <input>.
//
// Reuses `@googlemaps/js-api-loader` (already a dep via vue3-google-map) to
// pull in just the `places` library — no map is rendered. The key comes from
// `VITE_GOOGLE_MAPS_API_KEY`; with no key the composable is a no-op so the
// field stays plain manual entry (dev without a key keeps working).
//
// NOTE: uses the classic `places.Autocomplete`. Google flags it as legacy in
// favour of `PlaceAutocompleteElement`, but Autocomplete still ships and is
// the only variant that attaches to an existing input without a DOM rewrite.

// Module-level so the SDK loads once across every Address instance.
let placesPromise = null

const loadPlaces = () => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  if (!apiKey) return null

  if (!placesPromise) {
    // Functional API (js-api-loader v2); the old `Loader` class is deprecated.
    setOptions({ key: apiKey, v: 'weekly', libraries: ['places'] })
    placesPromise = importLibrary('places').catch((err) => {
      // Reset so a later attach can retry (e.g. transient network failure).
      placesPromise = null
      throw err
    })
  }
  return placesPromise
}

const componentValue = (components, type, key = 'long_name') =>
  components.find((c) => c.types.includes(type))?.[key] ?? ''

// Map a Google Place into our location field shape. State uses the full name
// (matches StateField's stored value); country uses the ISO code (matches the
// country listbox values).
const parsePlace = (place) => {
  const c = place?.address_components ?? []

  const streetNumber = componentValue(c, 'street_number')
  const route        = componentValue(c, 'route')

  return {
    address_1:   [streetNumber, route].filter(Boolean).join(' ').trim(),
    city:        componentValue(c, 'locality')
                 || componentValue(c, 'postal_town')
                 || componentValue(c, 'sublocality')
                 || componentValue(c, 'sublocality_level_1'),
    state:       componentValue(c, 'administrative_area_level_1'),
    postal_code: componentValue(c, 'postal_code'),
    country:     componentValue(c, 'country', 'short_name'),
  }
}

/**
 * @param {object}   opts
 * @param {import('vue').Ref<HTMLElement|null>} opts.inputRef  template ref to the address input
 * @param {(parsed: object) => void}            opts.onSelect  called with parsed fields on pick
 * @param {import('vue').Ref<string>|Function} [opts.country]  optional ISO code to bias results
 */
export function useGoogleAddressAutocomplete({ inputRef, onSelect, country }) {
  let autocomplete = null

  const currentCountry = () => (typeof country === 'function' ? country() : unref(country)) || null

  const attach = async (el) => {
    if (!el || autocomplete) return
    let places
    try {
      places = await loadPlaces()
    } catch {
      return // key invalid / load failed — stay manual entry
    }
    if (!places || !el.isConnected) return

    autocomplete = new places.Autocomplete(el, {
      types: ['address'],
      fields: ['address_components'],
      ...(currentCountry() ? { componentRestrictions: { country: currentCountry() } } : {}),
    })

    autocomplete.addListener('place_changed', () => {
      onSelect(parsePlace(autocomplete.getPlace()))
    })
  }

  const detach = () => {
    if (autocomplete && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(autocomplete)
    }
    autocomplete = null
  }

  // Re-bias suggestions when the chosen country changes.
  watch(
    () => currentCountry(),
    (iso) => autocomplete?.setComponentRestrictions(iso ? { country: iso } : null),
  )

  // Attach once the input element mounts; re-attach if the layout swaps it.
  watch(
    inputRef,
    (el) => { detach(); if (el) attach(el) },
    { immediate: true },
  )

  onBeforeUnmount(detach)
}
